import { useLingui } from "@lingui/react/macro";
import type { Bot, Me, ModelCatalogEntry, ModelCredential, ThinkingLevel } from "@rakazo/contracts";
import {
  chatModelChoices,
  connectedModelChoices,
  credentialForModel,
  groupModelChoices,
  MODEL_SEARCH_THRESHOLD,
  modelOptionKey,
  parseModelOptionKey,
  resolveSelectableModelId,
} from "@rakazo/core";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@rakazo/ui-web";
import { Check, ChevronDown } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { thinkingLevelLabel } from "../../lib/model-catalog";
import { rpc } from "../../lib/rpc";
import { toolbarButtonClass } from "./composer-toolbar";

type ModelMeta = { credentials: ModelCredential[]; catalog: ModelCatalogEntry[]; me: Me };

export type ComposerModelPatch = {
  modelProvider: string | null;
  modelId: string | null;
  thinkingLevel?: ThinkingLevel | null;
};

// Shared across composers so switching chats does not refetch the catalog.
let metaRequest: Promise<ModelMeta> | null = null;
function loadModelMeta(): Promise<ModelMeta> {
  metaRequest ??= Promise.all([rpc.models.credentials(), rpc.models.list(), rpc.me()])
    .then(([credentials, catalog, me]) => ({ credentials, catalog, me }))
    .catch((error: unknown) => {
      metaRequest = null;
      throw error;
    });
  return metaRequest;
}

/**
 * Kommerce: the bot's model and thinking level, changeable from the composer.
 * Saves to the bot through the same update the bot panel uses, so both stay in sync.
 */
export function ComposerModelControls({
  bot,
  disabled,
  onSave,
}: {
  bot: Bot;
  disabled?: boolean;
  onSave: (patch: ComposerModelPatch) => Promise<void>;
}) {
  const { t } = useLingui();
  const [meta, setMeta] = useState<ModelMeta | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");

  useEffect(() => {
    let live = true;
    loadModelMeta()
      .then((next) => live && setMeta(next))
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, []);

  const metaChoices = useMemo(() => {
    if (!meta) return [];
    const { credentials, catalog, me } = meta;
    const key =
      bot.modelProvider && bot.modelId
        ? modelOptionKey(
            bot.modelProvider,
            resolveSelectableModelId(catalog, bot.modelProvider, bot.modelId),
          )
        : "";
    const defaultEntry = me.defaultModel
      ? catalog.find((item) => item.provider === me.defaultProvider && item.id === me.defaultModel)
      : undefined;
    return chatModelChoices(
      connectedModelChoices(credentials, catalog),
      key,
      t`Space default`,
      defaultEntry?.images,
    );
  }, [meta, bot.modelProvider, bot.modelId, t]);
  const sections = useMemo(() => groupModelChoices(metaChoices, query), [metaChoices, query]);

  if (!meta) return null;
  const { credentials, catalog, me } = meta;

  const stored =
    bot.modelProvider && bot.modelId ? { provider: bot.modelProvider, modelId: bot.modelId } : null;
  const selected = stored
    ? {
        provider: stored.provider,
        modelId: resolveSelectableModelId(catalog, stored.provider, stored.modelId),
      }
    : null;
  const selectedKey = selected ? modelOptionKey(selected.provider, selected.modelId) : "";
  const choices = metaChoices;
  if (choices.length <= 1 && !selected) return null;

  const provider = selected?.provider ?? me.defaultProvider ?? null;
  const modelId = selected?.modelId ?? me.defaultModel ?? null;
  const entry =
    provider && modelId
      ? catalog.find(
          (item) =>
            item.provider === provider &&
            resolveSelectableModelId(catalog, item.provider, item.id) === modelId,
        )
      : undefined;
  const credential = credentialForModel(credentials, provider, modelId);
  const thinkingOptions = (credential?.thinkingLevels ?? entry?.thinkingLevels ?? []).filter(
    (level) => level !== "off",
  );
  const defaultThinking = credential?.thinkingLevel ?? "medium";

  async function save(patch: ComposerModelPatch) {
    setSaving(true);
    setError(null);
    try {
      await onSave(patch);
    } catch {
      setError(t`Could not change the model`);
    } finally {
      setSaving(false);
    }
  }

  const current = choices.find((choice) => choice.key === selectedKey) ?? choices[0];
  const busy = saving || disabled;
  const pickModel = (key: string) => {
    setOpen(false);
    setQuery("");
    const next = key ? parseModelOptionKey(key) : null;
    void save({ modelProvider: next?.provider ?? null, modelId: next?.modelId ?? null, thinkingLevel: null });
  };

  return (
    <div data-testid="composer-model-controls" className="flex min-w-0 items-center gap-0.5" title={error ?? undefined}>
      <Popover
        open={open}
        onOpenChange={(next) => {
          setOpen(next);
          if (!next) setQuery("");
        }}
      >
        <PopoverTrigger
          aria-label={t`Model`}
          disabled={busy}
          className={`${toolbarButtonClass} max-w-[14rem] ${error ? "text-destructive" : ""}`}
        >
          <span className="min-w-0 truncate">{current?.title ?? current?.label}</span>
          <ChevronDown size={14} className="shrink-0 opacity-70" />
        </PopoverTrigger>
        {open ? (
          <PopoverContent align="end" side="top" className="w-72 gap-0 overflow-hidden p-0">
            <Command shouldFilter={false} className="h-auto max-h-96 rounded-none bg-popover">
              {choices.length > MODEL_SEARCH_THRESHOLD ? (
                <CommandInput value={query} onValueChange={setQuery} placeholder={t`Search`} aria-label={t`Search models`} />
              ) : null}
              <CommandList className="max-h-80 overflow-y-auto">
                <CommandEmpty>{t`No matching models`}</CommandEmpty>
                {sections.map((section) => (
                  <CommandGroup key={section.key} heading={section.heading}>
                    {section.data.map((choice) => (
                      <CommandItem
                        key={choice.key}
                        value={choice.key || "space-default"}
                        data-checked={choice.key === selectedKey}
                        onSelect={() => pickModel(choice.key)}
                      >
                        <span className="min-w-0 flex-1 truncate">{choice.title ?? choice.label}</span>
                      </CommandItem>
                    ))}
                  </CommandGroup>
                ))}
              </CommandList>
            </Command>
          </PopoverContent>
        ) : null}
      </Popover>
      {thinkingOptions.length ? (
        <DropdownMenu>
          <DropdownMenuTrigger aria-label={t`Thinking`} disabled={busy} className={toolbarButtonClass}>
            {thinkingLevelLabel((bot.thinkingLevel as ThinkingLevel | null) ?? defaultThinking)}
            <ChevronDown size={14} className="shrink-0 opacity-70" />
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" side="top" className="w-48">
            {[null, ...thinkingOptions].map((level) => (
              <DropdownMenuItem
                key={level ?? "default"}
                onClick={() =>
                  void save({
                    modelProvider: selected?.provider ?? null,
                    modelId: selected?.modelId ?? null,
                    thinkingLevel: level,
                  })
                }
              >
                <span className="flex-1">
                  {level ? thinkingLevelLabel(level) : t`Default (${thinkingLevelLabel(defaultThinking)})`}
                </span>
                {(bot.thinkingLevel ?? null) === level ? <Check size={14} /> : null}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      ) : null}
    </div>
  );
}
